import { CrudFilters, DataProvider, useDataProvider } from "@refinedev/core";
import { useQuery } from "@tanstack/react-query";
import AmanotesIcon from "@src/assets/images/amanotes_icon.png";
import { IProduct } from "@src/types/user";

const DEFAULT_PAGE_SIZE = 100;

export const mapProductItem = (item: IProduct): IProduct => ({
  ...item,
  productCode: item.code,
  icon: item.icon || AmanotesIcon,
});

type FetchAllProductsOptions = {
  filters?: CrudFilters;
  pageSize?: number;
};

export const fetchAllProducts = async (
  dataProvider: DataProvider,
  { filters = [], pageSize = DEFAULT_PAGE_SIZE }: FetchAllProductsOptions = {},
): Promise<IProduct[]> => {
  const firstPage = await dataProvider.getList<IProduct>({
    resource: "products",
    pagination: { current: 1, pageSize },
    filters,
  });

  const total = firstPage.total ?? firstPage.data.length;
  const pageCount = Math.ceil(total / pageSize);

  if (pageCount <= 1) return firstPage.data.map(mapProductItem);

  const otherPages = await Promise.all(
    Array.from({ length: pageCount - 1 }, (_, index) =>
      dataProvider.getList<IProduct>({
        resource: "products",
        pagination: { current: index + 2, pageSize },
        filters,
      }),
    ),
  );

  return [...firstPage.data, ...otherPages.flatMap((page) => page.data)].map(mapProductItem);
};

type UseAllProductsOptions = {
  filters?: CrudFilters;
  enabled?: boolean;
};

export const useAllProducts = ({ filters = [], enabled = true }: UseAllProductsOptions = {}) => {
  const getDataProvider = useDataProvider();

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["products", "all", filters],
    enabled,
    queryFn: () => fetchAllProducts(getDataProvider("nestjsx"), { filters }),
  });

  return {
    products: data ?? [],
    isLoading,
    isFetching,
  };
};
